import { ComponentProps, FC, useEffect, useState } from 'react';

import { CheckedState } from '@radix-ui/react-checkbox';

import { cn } from '../lib/utils';
import { UICheckbox, UILabel } from '../ui';

export type CheckboxProps = ComponentProps<typeof UICheckbox> & {
  label?: string;
  description?: string;
  defaultChecked?: CheckedState;
  isDisabled?: boolean;
  variant?: 'default' | 'outlined';
  className?: string;
  dataTestId?: string;
  bgColorVar?: string;
};

const Checkbox: FC<CheckboxProps> = ({
  label,
  description,
  defaultChecked = false,
  isDisabled = false,
  variant = 'default',
  className,
  dataTestId,
  bgColorVar,
  id,
  ...rest
}) => {
  const [checked, setChecked] = useState<CheckedState>(defaultChecked);
  const checkboxId = id || 'toggle-checkbox';

  useEffect(() => {
    if (rest.checked !== undefined) {
      setChecked(rest.checked);
    }
  }, [rest.checked]);

  const handleCheckedChange = (checkedState: CheckedState) => {
    rest.onCheckedChange?.(!!checkedState);
    setChecked(checkedState);
  };

  const computedClassName = cn(
    'w-4 h-4 border flex items-center justify-center mt-0.5 box-content rounded',
    'data-[state=checked]:border-base-primary',
    'data-[state=indeterminate]:border-base-primary',
    '[&>span>svg]:h3',
    '[&>span>svg]:w-3',
    'data-[state=unchecked]:bg-base-background data-[state=unchecked]:border-base-primary data-[state=unchecked]:shadow-none',
    'data-[state=disabled]:bg-muted-foreground data-[state=disabled]:transparent data-[state=disabled]:text-bg-muted-foreground data-[state=disabled]:shadow-none data-[state=checked]:shadow-none data-[state=indeterminate]:shadow-none',
    variant === 'outlined'
      ? 'data-[state=checked]:bg-base-background data-[state=checked]:text-base-primary data-[state=unchecked]:bg-base-background data-[state=indeterminate]:bg-base-background data-[state=indeterminate]:text-base-primary'
      : !bgColorVar
        ? 'data-[state=checked]:bg-base-primary data-[state=checked]:text-base-background data-[state=indeterminate]:bg-base-primary data-[state=indeterminate]:text-base-background'
        : 'data-[state=checked]:text-base-background data-[state=indeterminate]:text-base-background',
    !label && 'mt-0',
    className
  );

  const checkboxStyle = bgColorVar && checked !== false ? {
    backgroundColor: `var(${bgColorVar})`,
  } : undefined;

  return (
    <div className={'flex space-x-2'}>
      <UICheckbox
        {...rest}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        id={checkboxId}
        disabled={isDisabled}
        className={computedClassName}
        style={checkboxStyle}
        data-testid={dataTestId}
      />

      <div className="flex flex-col">
        {label && (
          <UILabel
            htmlFor={checkboxId}
            className={cn(
              'text-p-s cursor-pointer',
              isDisabled ? 'text-base-mutedForeground' : 'text-base-foreground'
            )}
          >
            {label}
          </UILabel>
        )}
        {description && (
          <span
            className={cn(
              'text-p-s text-base-mutedForeground mt-1.5',
              !label && 'mt-0'
            )}
          >
            {description}
          </span>
        )}
      </div>
    </div>
  );
};

export default Checkbox;
